import { Component, ElementRef, NgModule, ViewChild } from '@angular/core';
import { CommonModule, NgClass, AsyncPipe } from '@angular/common';
import { combineLatest, Subject, map, startWith, switchMap, take, tap } from 'rxjs';
import { MatDialogModule, MatDialogRef, MatDialogClose } from '@angular/material/dialog';
import { A11yModule } from '@angular/cdk/a11y';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule, MatIcon } from '@angular/material/icon';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { AdminService } from '@uw/core/services/admin.service';
import { ProfileInfoResponse } from '@uw/core/api/models/profile-info-response';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MetadataService } from '@uw/core/services/metadata.service';
import { PostalZipInputModule } from '@uw/shared/directives/postal-zip-input';
import { isFieldValidfn } from '@uw/core/utils/util-functions';
import { SharedModule } from '@uw/shared/shared.module';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { customEmailValidator } from '@uw/shared/validators/custom-email-validator';
import { FieldErrorDisplayComponent } from '../field-error-display/field-error-display.component';


@Component({
    selector: 'uw-profile-information-dialog',
    templateUrl: './profile-information-dialog.component.html',
    styleUrls: ['./profile-information-dialog.component.scss'],
    imports: [A11yModule, MatIconButton, MatDialogClose, MatIcon, ReactiveFormsModule, FieldErrorDisplayComponent, NgxMaskDirective, NgClass, AsyncPipe, TranslateModule]
})
export class ProfileInformationDialogComponent {

  @ViewChild('closeBtn') closeBtn!: ElementRef<HTMLButtonElement>;

  readonly isFieldValid = isFieldValidfn;

  private readonly _saveSubject: Subject<ProfileInfoResponse> = new Subject();

  constructor(
    private readonly _adminService: AdminService,
    private readonly _dialogRef: MatDialogRef<ProfileInformationDialogComponent>,
    private readonly _metadataService: MetadataService
  ) {
    this._dialogRef.afterOpened().pipe(
      switchMap(() => this.vm$.pipe(take(1)))
    ).subscribe(() => setTimeout(() => this.closeBtn?.nativeElement?.focus()));
  }

  ngOnInit() {
    this.form.disable();
  }


  readonly form = new UntypedFormGroup({
    address: new UntypedFormControl(null, { validators: [Validators.required] }),
    city: new UntypedFormControl(null, { validators: [Validators.required] }),
    country: new UntypedFormControl("CA", { validators: [Validators.required] }),
    postalZip: new UntypedFormControl(null, { validators: [Validators.required] }),
    provinceState: new UntypedFormControl(null, { validators: [Validators.required] }),

    doNotContact: new UntypedFormControl(null, []),
    firstName: new UntypedFormControl(null, { validators: [Validators.required] }),
    languagePref: new UntypedFormControl(null, { validators: [Validators.required] }),
    lastName: new UntypedFormControl(null, { validators: [Validators.required] }),
    personalEmail: new UntypedFormControl(null, { validators: [Validators.required, customEmailValidator()] }),
    personalPhoneNumber: new UntypedFormControl(null, { validators: [Validators.required] }),
    receiveEmails: new UntypedFormControl(0, []),
    receiveEmailsBool: new UntypedFormControl(false, []),
    receivesTextMessages: new UntypedFormControl(0, []),
    receivesTextMessagesBool: new UntypedFormControl(false, []),
    salutation: new UntypedFormControl(null, []),
    userName: new UntypedFormControl(null, []),
    workEmail: new UntypedFormControl(null, { validators: [Validators.required, customEmailValidator()] }),
    workPhoneExt: new UntypedFormControl(null, [Validators.pattern("^[0-9]{1,10}$")]),
    workPhoneNumber: new UntypedFormControl(null, { validators: [Validators.required] }),
  });


  public vm$ = combineLatest([
    this._adminService.getProfile().pipe(
      tap(profile => {
        this.form.patchValue(profile, { emitEvent: false })
      }),
    ),
    this.form.get("country")!.valueChanges.pipe(
      startWith("CA"),
      switchMap(countryCode => this._metadataService.getProvinceList(countryCode))
    ),
    this._metadataService.getCountryList(),
    this._saveSubject.pipe(
      switchMap(profile => this._adminService.updateProfile(profile)),
      tap(profile => this._dialogRef.close(profile)),
      startWith(null)
    )
  ]).pipe(
    map(([profile, provinces, countries]) => {
      return {
        provinces,
        countries
      };
    })
  )

  save(request: ProfileInfoResponse) {
    this._saveSubject.next(request);
  }
}

@NgModule({
    exports: [
        ProfileInformationDialogComponent
    ],
    imports: [
        CommonModule,
        MatDialogModule,
        ReactiveFormsModule,
        MatIconModule,
        MatButtonModule,
        TranslateModule,
        MatCheckboxModule,
        PostalZipInputModule,
        SharedModule,
        NgxMaskDirective, NgxMaskPipe,
        ProfileInformationDialogComponent
    ],
    providers: [provideNgxMask()]
})
export class ProfileInformationDialogModule { }
